'use client'

import type { Image } from 'cms-types'

import Button from '../../components/Button'
import Icon from '../../components/Icon'
import LoadingText from '../../components/LoadingText'
import { PayloadImage } from '../../components/PayloadImage'
import type { LoadableComponentProps } from '../../types'

export type ClubJoinStatus = 'idle' | 'joining' | 'success' | 'error'

export interface ClubJoinData {
  title: string
  subtitle: string
  benefitsTitle?: string
  benefits?: { text?: string | null }[] | null
  joinButtonLabel: string
  image?: Image
  successTitle: string
  successText: string
  errorText: string
  status: ClubJoinStatus
  onJoin: () => void
}

export default function ClubJoin({ data, loading }: LoadableComponentProps<ClubJoinData>) {
  const {
    title,
    subtitle,
    benefitsTitle,
    benefits = [],
    joinButtonLabel,
    image,
    successTitle,
    successText,
    errorText,
    status = 'idle',
    onJoin,
  } = data || {}

  const visibleBenefits = (benefits ?? []).filter(
    (benefit): benefit is { text: string } => !!benefit?.text,
  )

  const isSuccess = status === 'success'

  return (
    <div className='w-full overflow-hidden bg-steelblue'>
      <section className='relative w-full text-white'>
        <PayloadImage
          src={image}
          sizes='100vw'
          objectFit='cover'
          className='!absolute inset-0'
          showPlaceholder={loading}
        />
        {/* Darkening overlay so the white headline stays legible on any image. */}
        {!loading && <div className='absolute inset-0 bg-black/30' />}

        <div className='relative mx-auto max-w-4xl px-6 py-20 text-center'>
          <h1 className='h2 tracking-wide'>{loading ? <LoadingText textLength={18} /> : title}</h1>
          <p className='md mt-3 text-white'>
            {loading ? <LoadingText textLength={80} lines={2} /> : subtitle}
          </p>
        </div>
      </section>

      <section className='relative mx-auto flex w-full max-w-2xl flex-col items-center gap-10 px-6 pb-16 pt-14 text-center text-black'>
        {!isSuccess && visibleBenefits.length > 0 && (
          <div className='rounded-2xl w-full bg-white px-6 py-8 text-left shadow-sm sm:px-10'>
            {benefitsTitle && <p className='h6 tracking-wide text-highlight'>{benefitsTitle}</p>}
            <ul className='mt-5 flex flex-col gap-4'>
              {visibleBenefits.map((benefit, index) => (
                <li key={index} className='flex items-start gap-3'>
                  <Icon name='MdCheckCircle' size={24} className='mt-0.5 shrink-0 text-highlight' />
                  <p className='md text-black'>{benefit.text}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {isSuccess ? (
          <div
            className='flex flex-col items-center gap-3'
            role='status'
            data-testid='club-join-success'
          >
            <Icon name='MdCheckCircle' size={40} className='text-highlight' />
            <p className='h4 tracking-wide'>{successTitle}</p>
            <p className='md max-w-xl text-black'>{successText}</p>
          </div>
        ) : (
          <div className='flex w-full flex-col items-center gap-4'>
            <Button
              appearance='primary'
              onPress={onJoin}
              isLoading={loading || status === 'joining'}
              isDisabled={loading || status === 'joining'}
              className='!max-w-none'
            >
              {loading ? '' : joinButtonLabel}
            </Button>

            {status === 'error' && (
              <p className='sm text-highlight' role='alert' data-testid='club-join-error'>
                {errorText}
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
